'use strict';

import { Entity } from '../Entity';
import { User } from '../User/User';
import { UserEnrollmentVendorSession } from '../User/UserEnrollmentVendorSession';
import { UserLead } from '../User/UserLead';
import { UserSoa } from '../User/UserSoa';
import { UserSources } from '../User/UserSources';
import { IndAddress } from './IndAddress';
import { IndAlternateId } from './IndAlternateId';
import { IndAppAlternateId } from './IndAppAlternateId';
import { IndConnection } from './IndConnection';
import { IndDependent } from './IndDependent';
import { IndDrug2 } from './IndDrug2';
import { IndEmail } from './IndEmail';
import { IndEmployer } from './IndEmployer';
import { IndHealth } from './IndHealth';
import { IndMedicaid } from './IndMedicaid';
import { IndMedicare } from './IndMedicare';
import { IndPharmacy } from './IndPharmacy';
import { IndPhone } from './IndPhone';
import { IndPlanYearReview } from './IndPlanYearReview';
import { IndPolicy } from './IndPolicy';
import { IndPortalLog } from './IndPortalLog';
import { IndPrefLang } from './IndPrefLang';
import { IndProvider } from './IndProvider';
import { IndSocial } from './IndSocial';
import { IndSpap } from './IndSpap';

import { enumOptions } from '@app/Utilities/Enum';
import type { TFunction } from 'i18next';
import { IndAddressCollection } from '../../Collections/Ind/IndAddressCollection';
import { IndAlternateIdCollection } from '../../Collections/Ind/IndAlternateIdCollection';
import { IndAppAlternateIdCollection } from '../../Collections/Ind/IndAppAlternateIdCollection';
import { IndConnectionCollection } from '../../Collections/Ind/IndConnectionCollection';
import { IndDependentCollection } from '../../Collections/Ind/IndDependentCollection';
import { IndDrug2Collection } from '../../Collections/Ind/IndDrug2Collection';
import { IndEmailCollection } from '../../Collections/Ind/IndEmailCollection';
import { IndFlagCollection } from '../../Collections/Ind/IndFlagCollection';
import { IndPharmacyCollection } from '../../Collections/Ind/IndPharmacyCollection';
import { IndPhoneCollection } from '../../Collections/Ind/IndPhoneCollection';
import { IndPlanYearReviewCollection } from '../../Collections/Ind/IndPlanYearReviewCollection';
import { IndPolicyCollection } from '../../Collections/Ind/IndPolicyCollection';
import { IndPortalLogCollection } from '../../Collections/Ind/IndPortalLogCollection';
import { IndProviderCollection } from '../../Collections/Ind/IndProviderCollection';
import { UserSoaCollection } from '../../Collections/User/UserSoaCollection';
import { IndFlag } from './IndFlag';

// Gender
export enum IndGender {
  Male = 'M',
  Female = 'F',
}

export const getIndGenderSelectOptions = (t: TFunction) =>
  enumOptions(
    [
      { value: IndGender.Male, labelKey: 'ind.gender.male' },
      { value: IndGender.Female, labelKey: 'ind.gender.female' },
    ],
    t
  );

// Status
export enum IndStatus {
  Active = 'Active Client',
  Inactive = 'Inactive Client',
  Prospect = 'Prospect',
  InactiveProspect = 'Inactive Prospect',
  Deceased = 'Deceased',
  NonCommissionableClient = 'Non-Commissionable Client',
}

export const getIndStatusSelectOptions = (t: TFunction) =>
  enumOptions(
    [
      { value: IndStatus.Active, labelKey: 'ind.status.active' },
      { value: IndStatus.Inactive, labelKey: 'ind.status.inactive' },
      { value: IndStatus.Prospect, labelKey: 'ind.status.prospect' },
      { value: IndStatus.InactiveProspect, labelKey: 'ind.status.inactiveProspect' },
      { value: IndStatus.Deceased, labelKey: 'ind.status.deceased' },
      { value: IndStatus.NonCommissionableClient, labelKey: 'ind.status.nonCommissionableClient' },
    ],
    t
  );

// Prefix
export enum IndPrefix {
  Ms = 'Ms',
  Miss = 'Miss',
  Mrs = 'Mrs',
  Mr = 'Mr',
  Master = 'Master',
  Fr = 'Fr',
  Rev = 'Rev',
  Dr = 'Dr',
  Atty = 'Atty',
  Hon = 'Hon',
  Prof = 'Prof',
  Pres = 'Pres',
  VP = 'VP',
  Gov = 'Gov',
  Ofc = 'Ofc',
  Rabbi = 'Rabbi',
  Rebetzin = 'Rebetzin',
  Pastor = 'Pastor',
  Sr = 'Sr',
}

export const getIndPrefixSelectOptions = (t: TFunction) =>
  enumOptions(
    [
      { value: IndPrefix.Ms, labelKey: 'ind.prefix.ms' },
      { value: IndPrefix.Miss, labelKey: 'ind.prefix.miss' },
      { value: IndPrefix.Mrs, labelKey: 'ind.prefix.mrs' },
      { value: IndPrefix.Mr, labelKey: 'ind.prefix.mr' },
      { value: IndPrefix.Master, labelKey: 'ind.prefix.master' },
      { value: IndPrefix.Fr, labelKey: 'ind.prefix.fr' },
      { value: IndPrefix.Rev, labelKey: 'ind.prefix.rev' },
      { value: IndPrefix.Dr, labelKey: 'ind.prefix.dr' },
      { value: IndPrefix.Atty, labelKey: 'ind.prefix.atty' },
      { value: IndPrefix.Hon, labelKey: 'ind.prefix.hon' },
      { value: IndPrefix.Prof, labelKey: 'ind.prefix.prof' },
      { value: IndPrefix.Pres, labelKey: 'ind.prefix.pres' },
      { value: IndPrefix.VP, labelKey: 'ind.prefix.vp' },
      { value: IndPrefix.Gov, labelKey: 'ind.prefix.gov' },
      { value: IndPrefix.Ofc, labelKey: 'ind.prefix.ofc' },
      { value: IndPrefix.Rabbi, labelKey: 'ind.prefix.rabbi' },
      { value: IndPrefix.Rebetzin, labelKey: 'ind.prefix.rebetzin' },
      { value: IndPrefix.Pastor, labelKey: 'ind.prefix.pastor' },
      { value: IndPrefix.Sr, labelKey: 'ind.prefix.sr' },
    ],
    t
  );

// Suffix
export enum IndSuffix {
  Jr = 'Jr.',
  Sr = 'Sr.',
  II = 'II',
  III = 'III',
  IV = 'IV',
  V = 'V',
}

export const getIndSuffixSelectOptions = (t: TFunction) =>
  enumOptions(
    [
      { value: IndSuffix.Jr, labelKey: 'ind.suffix.jr' },
      { value: IndSuffix.Sr, labelKey: 'ind.suffix.sr' },
      { value: IndSuffix.II, labelKey: 'ind.suffix.ii' },
      { value: IndSuffix.III, labelKey: 'ind.suffix.iii' },
      { value: IndSuffix.IV, labelKey: 'ind.suffix.iv' },
      { value: IndSuffix.V, labelKey: 'ind.suffix.v' },
    ],
    t
  );

// Marital Status
export enum IndMaritalStatus {
  Married = 'Married',
  Separated = 'Separated',
  Divorced = 'Divorced',
  Widowed = 'Widowed',
  Single = 'Single',
  DomesticPartner = 'Domestic Partner',
}

export const indMaritalStatus = Object.values(IndMaritalStatus) as Array<IndMaritalStatus>;
export const getIndMaritalStatusSelectOptions = (t: TFunction) =>
  enumOptions(
    [
      { value: IndMaritalStatus.Married, labelKey: 'ind.maritalStatus.married' },
      { value: IndMaritalStatus.Separated, labelKey: 'ind.maritalStatus.separated' },
      { value: IndMaritalStatus.Divorced, labelKey: 'ind.maritalStatus.divorced' },
      { value: IndMaritalStatus.Widowed, labelKey: 'ind.maritalStatus.widowed' },
      { value: IndMaritalStatus.Single, labelKey: 'ind.maritalStatus.single' },
      { value: IndMaritalStatus.DomesticPartner, labelKey: 'ind.maritalStatus.domesticPartner' },
    ],
    t
  );

// Designation
export enum IndDesignation {
  Veteran = 'Veteran',
  VeteranSpouse = 'Veteran spouse',
  LawEnforcement = 'Law Enforcement',
  FirstResponder = 'First Responder',
  Missionary = 'Missionary',
  AmericanNativeIndianTribe = 'American Native Indian Tribe',
  FederalRetiree = 'Federal Retiree',
  StateRetiree = 'State Retiree',
  Disabled = 'Disabled',
  None = 'None',
}

export const getIndDesignationSelectOptions = (t: TFunction) =>
  enumOptions(
    [
      { value: IndDesignation.Veteran, labelKey: 'ind.designation.veteran' },
      { value: IndDesignation.VeteranSpouse, labelKey: 'ind.designation.veteranSpouse' },
      { value: IndDesignation.LawEnforcement, labelKey: 'ind.designation.lawEnforcement' },
      { value: IndDesignation.FirstResponder, labelKey: 'ind.designation.firstResponder' },
      { value: IndDesignation.Missionary, labelKey: 'ind.designation.missionary' },
      {
        value: IndDesignation.AmericanNativeIndianTribe,
        labelKey: 'ind.designation.americanNativeIndianTribe',
      },
      { value: IndDesignation.FederalRetiree, labelKey: 'ind.designation.federalRetiree' },
      { value: IndDesignation.StateRetiree, labelKey: 'ind.designation.stateRetiree' },
      { value: IndDesignation.Disabled, labelKey: 'ind.designation.disabled' },
      { value: IndDesignation.None, labelKey: 'ind.designation.none' },
    ],
    t
  );

// Resident Status
export enum IndResident {
  Citizen = 'Citizen',
  PermanentResident = 'Permanent Resident',
  ImmigrationProcess = 'Immigration process',
  Other = 'Other',
}

export const getIndResidentSelectOptions = (t: TFunction) =>
  enumOptions(
    [
      { value: IndResident.Citizen, labelKey: 'ind.resident.citizen' },
      { value: IndResident.PermanentResident, labelKey: 'ind.resident.permanentResident' },
      { value: IndResident.ImmigrationProcess, labelKey: 'ind.resident.immigrationProcess' },
      { value: IndResident.Other, labelKey: 'ind.resident.other' },
    ],
    t
  );

export class Ind extends Entity {
  protected indId;
  protected ownerId;
  protected indGender: IndGender;
  protected indStatus: IndStatus;
  protected indPrefix: IndPrefix | null;
  protected indFirstname;
  protected indMiddlename;
  protected indLastname;
  protected indSuffix: IndSuffix | null;
  protected indNickname;
  protected indDob;
  protected indSource: number;
  protected indSpouse = '0';
  protected indDesignation;
  protected indFlagged = '0';
  protected indOccupation: string;
  protected indFacts;
  protected indMaritalStatus;
  protected partyId;
  protected loa;
  protected indReferral;
  protected indResident;
  protected indReferralTo;
  protected indPreferredContactMethod;
  protected indProfilePic;
  protected indIdCrypto;
  protected dateTimeCreated;
  protected lastUpdateTime;
  protected externalId;

  protected indDobFormatted = '';

  // Computed
  protected policies;
  protected providers;
  protected pharmacies;
  protected flags: IndFlagCollection;
  protected rx;
  protected emails: IndEmailCollection;
  protected phones: IndPhoneCollection;
  protected addresses: IndAddressCollection;
  protected source;
  protected lead;
  protected medicare: IndMedicare;
  protected medicaid: IndMedicaid;
  protected prefLang: IndPrefLang;
  protected spap: IndSpap;
  protected health: IndHealth;
  protected social: IndSocial;
  protected spouse: Ind;
  protected indAge;
  protected alternateIds;
  protected alternateAppIds;
  protected connections;
  protected dependents;
  protected soas;
  protected employer: IndEmployer;
  protected portalLogs;
  protected planYearReviews;
  protected vendorSession: UserEnrollmentVendorSession;

  constructor() {
    super();
  }

  public hydrate(data: any) {
    this.indId = data.indId;
    // this.ownerId = data.ownerId,
    this.indGender = data.indGender;
    this.indStatus = data.indStatus;
    this.indFirstname = data.indFirstname;
    this.indMiddlename = data.indMiddlename;
    this.indLastname = data.indLastname;
    this.indPrefix = data.indPrefix === '' ? null : data.indPrefix;
    this.indSuffix = data.indSuffix === '' ? null : data.indSuffix;
    this.indNickname = data.indNickname;
    this.indDob = data.indDob;
    this.indSource = data.indSource;
    this.indDesignation = data.indDesignation;
    this.indFlagged = data.indFlagged;
    this.indFacts = data.indFacts;
    this.indMaritalStatus = data.indMaritalStatus;
    this.indSource = data.indSource ? Number(data.indSource) : undefined;
    this.partyId = data.partyId;
    //this.loa = data.loa,
    this.indReferral = data.indReferral;
    this.indResident = data.indResident;
    this.indReferralTo = data.indReferralTo;
    this.indPreferredContactMethod = data.indPreferredContactMethod;
    this.indProfilePic = data.indProfilePic;
    this.indIdCrypto = data.indIdCrypto;
    this.indOccupation = data.indOccupation;
    this.externalId = data.externalId;
    //this.dateTimeCreated = data.dateTimeCreated.date
    //this.indAge = this.getIndAge(this.indDob)

    if (typeof data.ownerId === 'number') {
      this.ownerId = data.ownerId;
    } else if (data.ownerId && typeof data.ownerId === 'object') {
      const ownerId = new User();

      ownerId.hydrate(data.ownerId);

      this.ownerId = ownerId;
    }

    if (data.loa) {
      if (typeof data.loa === 'number') {
        this.loa = data.loa;
      } else if (typeof data.loa === 'object') {
        const loa = new User();

        loa.hydrate(data.loa);

        this.loa = loa;
      }
    }

    if (data.spouse) {
      const spouse = new Ind();

      spouse.hydrate(data.spouse);

      this.spouse = spouse;
    }

    if (data.dateTimeCreated) {
      const split = data.dateTimeCreated.date.split('.');
      this.dateTimeCreated = split[0];
    }
    if (data.lastUpdateTime) {
      const split = data.lastUpdateTime.date.split('.');
      this.lastUpdateTime = split[0];
    }

    if (data.connections) {
      const connections = new IndConnectionCollection();

      data.connections.forEach((singleConnectionData) => {
        const connection = new IndConnection();
        connection.hydrate(singleConnectionData);

        connections.addItem(connection);
      });

      this.connections = connections;
    }

    if (data.flags) {
      const flags = new IndFlagCollection();

      data.flags.forEach((singleFlagData) => {
        const flag = new IndFlag();
        flag.hydrate(singleFlagData);

        flags.addItem(flag);
      });

      this.flags = flags;
    }

    if (data.dependents) {
      const dependents = new IndDependentCollection();

      data.dependents.forEach((singleDependentData) => {
        const dependent = new IndDependent();
        dependent.hydrate(singleDependentData);

        dependents.addItem(dependent);
      });

      this.dependents = dependents;
    }

    if (data.pharmacies) {
      const pharmacies = new IndPharmacyCollection();

      data.pharmacies.forEach((singlePharmacyData) => {
        const pharmacy = new IndPharmacy();
        pharmacy.hydrate(singlePharmacyData);

        pharmacies.addItem(pharmacy);
      });

      this.pharmacies = pharmacies;
    }

    if (data.flags) {
      const flags = new IndFlagCollection();

      data.flags.forEach((singleFlagData) => {
        const flag = new IndFlag();
        flag.hydrate(singleFlagData);

        flags.addItem(flag);
      });

      this.flags = flags;
    }

    if (data.indPortalLogs) {
      const portalLogs = new IndPortalLogCollection();

      data.indPortalLogs.forEach((singlePortalLogData) => {
        const portalLog = new IndPortalLog();
        portalLog.hydrate(singlePortalLogData);

        portalLogs.addItem(portalLog);
      });

      this.portalLogs = portalLogs;
    }

    if (data.planYearReviews) {
      const planYearReviews = new IndPlanYearReviewCollection();

      data.planYearReviews.forEach((singlePlanYearReviewData) => {
        const planYearReview = new IndPlanYearReview();
        planYearReview.hydrate(singlePlanYearReviewData);

        planYearReviews.addItem(planYearReview);
      });

      this.planYearReviews = planYearReviews;
    }

    if (data.soas) {
      const soas = new UserSoaCollection();

      data.soas.forEach((singleSoaData) => {
        const soa = new UserSoa();
        soa.hydrate(singleSoaData);

        soas.addItem(soa);
      });

      this.soas = soas;
    }

    if (data.addresses) {
      const addresses = new IndAddressCollection();

      data.addresses.forEach((singleAddressData) => {
        const address = new IndAddress();
        address.hydrate(singleAddressData);

        addresses.addItem(address);
      });

      this.addresses = addresses;
    }

    if (data.policies) {
      const policies = new IndPolicyCollection();

      data.policies.forEach((singlePolicyData) => {
        const policy = new IndPolicy();
        policy.hydrate(singlePolicyData);

        policies.addItem(policy);
      });

      this.policies = policies;
    }

    if (data.providers) {
      const providers = new IndProviderCollection();

      data.providers.forEach((singleProviderData) => {
        const provider = new IndProvider();
        provider.hydrate(singleProviderData);

        providers.addItem(provider);
      });

      this.providers = providers;
    }

    if (data.rx) {
      const rxs = new IndDrug2Collection();

      data.rx.forEach((singleRxData) => {
        const rx = new IndDrug2();
        rx.hydrate(singleRxData);

        rxs.addItem(rx);
      });

      this.rx = rxs;
    }

    if (data.source) {
      const userSources = new UserSources();

      userSources.hydrate(data.source);

      this.source = userSources;
    }

    if (data.spouse) {
      const spouse = new Ind();

      spouse.hydrate(data.spouse);

      this.spouse = spouse;
    }

    if (data.phones) {
      const phones = new IndPhoneCollection();

      data.phones.forEach((singlePhoneData) => {
        const phone = new IndPhone();
        phone.hydrate(singlePhoneData);

        phones.addItem(phone);
      });

      this.phones = phones;
    }

    if (data.flags) {
      const flags = new IndFlagCollection();

      data.flags.forEach((singlePhoneData) => {
        const flag = new IndFlag();
        flag.hydrate(singlePhoneData);

        flags.addItem(flag);
      });

      this.flags = flags;
    }

    if (data.lead) {
      const lead = new UserLead();

      lead.hydrate(data.lead);

      this.lead = lead;
    }

    if (data.medicare) {
      const medicare = new IndMedicare();

      medicare.hydrate(data.medicare);

      this.medicare = medicare;
    }

    if (data.medicaid) {
      const medicaid = new IndMedicaid();

      medicaid.hydrate(data.medicaid);

      this.medicaid = medicaid;
    }

    if (data.vendorSession) {
      const vendorSession = new UserEnrollmentVendorSession();

      vendorSession.hydrate(data.vendorSession);

      this.vendorSession = vendorSession;
    }

    if (data.spap) {
      const spap = new IndSpap();

      spap.hydrate(data.spap);

      this.spap = spap;
    }

    if (data.health) {
      const health = new IndHealth();

      health.hydrate(data.health);

      this.health = health;
    }

    if (data.social) {
      const social = new IndSocial();

      social.hydrate(data.social);

      this.social = social;
    }

    if (data.employer) {
      const employer = new IndEmployer();

      employer.hydrate(data.employer);

      this.employer = employer;
    }

    if (data.prefLang) {
      const prefLang = new IndPrefLang();

      prefLang.hydrate(data.prefLang);

      this.prefLang = prefLang;
    }

    if (data.emails) {
      const emails = new IndEmailCollection();

      data.emails.forEach((singleEmailData) => {
        const address = new IndEmail();
        address.hydrate(singleEmailData);

        emails.addItem(address);
      });

      this.emails = emails;
    }

    if (data.alternateIds) {
      const alternateIds = new IndAlternateIdCollection();

      data.alternateIds.forEach((singleAlternateData) => {
        const alternateId = new IndAlternateId();
        alternateId.hydrate(singleAlternateData);

        alternateIds.addItem(alternateId);
      });

      this.alternateIds = alternateIds;
    }

    if (data.alternateAppIds) {
      const alternateAppIds = new IndAppAlternateIdCollection();

      data.alternateAppIds.forEach((singleAlternateData) => {
        const alternateAppId = new IndAppAlternateId();
        alternateAppId.hydrate(singleAlternateData);

        alternateAppIds.addItem(alternateAppId);
      });

      this.alternateAppIds = alternateAppIds;
    }

    if (data.soas) {
      const soas = new UserSoaCollection();

      data.soas.forEach((singleSoaData) => {
        const soa = new UserSoa();
        soa.hydrate(singleSoaData);

        soas.addItem(soa);
      });

      this.soas = soas;
    }
  }

  public getSource() {
    return this.source;
  }

  public getLead() {
    return this.lead;
  }

  public getVendorSession() {
    return this.vendorSession;
  }

  public getMedicare() {
    return this.medicare;
  }

  public getPrefLang() {
    return this.prefLang;
  }

  public getMedicaid() {
    return this.medicaid;
  }

  public getSpap() {
    return this.spap;
  }

  public getHealth() {
    return this.health;
  }

  public getSocial() {
    return this.social;
  }

  public getEmployer() {
    return this.employer;
  }

  public getSpouse() {
    return this.spouse;
  }

  public getEmails() {
    return this.emails;
  }

  public getPhones() {
    return this.phones;
  }

  public getPolicies() {
    return this.policies;
  }

  public getAddresses() {
    return this.addresses;
  }

  public getProviders() {
    return this.providers;
  }

  public getRx() {
    return this.rx;
  }

  public getPharmacies() {
    return this.pharmacies;
  }

  public getAlternateIds() {
    return this.alternateIds;
  }

  public getFlagsColors(): Set<string> {
    return new Set(
      this.getFlags()
        .getItems()
        .map((f) => f.getIndFlagged())
    );
  }

  public getAlternateAppIds() {
    return this.alternateAppIds;
  }

  public getSoas() {
    return this.soas;
  }

  public getPlanYearReviews() {
    return this.planYearReviews;
  }

  public getAnyAddress() {
    let address = null;

    address = this.getDefaultAddress();

    if (!address && this.spouse) {
      address = this.spouse.getDefaultAddress();
    }

    if (!address) {
      address = this.getDefaultAddress();
    }

    if (!address && this.spouse) {
      address = this.spouse.getDefaultAddress();
    }

    if (!address) {
      var addressAll = this.getAddresses().getItems();

      if (addressAll.length > 0) {
        address = addressAll[0];
      }
    }

    if (!address && this.spouse) {
      var addressAll = this.spouse.getAddresses().getItems();

      if (addressAll.length > 0) {
        address = addressAll[0];
      }
    }

    return address;
  }

  public getAnyAddressConcat() {
    let addressConcat = '';

    const address = this.getAnyAddress();

    if (address) {
      addressConcat =
        address.getIndAddressStreet() +
        (address.getIndAddressUnit() ? ' ' + address.getIndAddressUnit() : '') +
        ' ' +
        address.getIndAddressCity() +
        ' ' +
        address.getIndAddressState() +
        ' ' +
        address.getIndAddressZip();
    }
    return addressConcat;
  }

  public getAnyDefaultAddress() {
    let address = null;

    address = this.getDefaultAddress();

    if (!address && this.spouse) {
      address = this.spouse.getDefaultAddress();
    }

    if (!address) {
      address = this.getMailingAddress();
    }

    if (!address && this.spouse) {
      address = this.spouse.getMailingAddress();
    }

    if (!address) {
      address = this.getAddresses()[0];
    }

    if (!address && this.spouse) {
      address = this.spouse.getAddresses()[0];
    }

    return address;
  }

  public getAnyMailingAddress() {
    let address = null;

    address = this.getMailingAddress();

    if (!address && this.spouse) {
      address = this.spouse.getMailingAddress();
    }

    if (!address) {
      address = this.getDefaultAddress();
    }

    if (!address && this.spouse) {
      address = this.spouse.getDefaultAddress();
    }

    if (!address) {
      address = this.getAddresses()[0];
    }

    if (!address && this.spouse) {
      address = this.spouse.getAddresses()[0];
    }

    return address;
  }

  public getDefaultAddress(): IndAddress | null {
    let selectedAddress = null;

    if (this.addresses) {
      this.addresses.getItems().forEach((address) => {
        if (address.getIndAddressPref()) {
          selectedAddress = address;
        }
      });
    }
    return selectedAddress;
  }

  public getMailingAddress() {
    let selectedAddress = null;

    if (this.addresses) {
      this.addresses.getItems().forEach((address) => {
        if (address.getIndAddressType() === 'Mailing') {
          selectedAddress = address;
        }
      });
    }
    return selectedAddress;
  }

  public getDefaultEmail(): IndEmail | null {
    let selectedEmail = null;

    if (this.emails) {
      this.emails.getItems().forEach((email) => {
        /*if (email.getIndEmailPref() && !email.getIndEmailOptOut()) {
                    selectedEmail = email;
                }*/
        if (email.getIndEmailPref() == '1') {
          selectedEmail = email;
        }
      });
    }

    return selectedEmail;
  }

  public getAnyEmail() {
    let selectedEmail = null;

    selectedEmail = this.getDefaultEmail();

    if (!selectedEmail) {
      if (this.emails) {
        const anyEmail = this.emails.getItems()[0];

        if (anyEmail && !anyEmail.getIndEmailOptOut()) {
          selectedEmail = anyEmail;
        }
      }

      /*if ( anyEmail ) {
                selectedEmail = anyEmail;
            }*/
    }

    return selectedEmail;
  }

  public getDefaultPhone(): IndPhone | null {
    let selectedPhone = null;

    if (this.phones) {
      this.phones.getItems().forEach((phone) => {
        /*if (phone.getIndPhonePref() && !phone.getIndPhoneDnc()) {
                    selectedPhone = phone;
                }*/
        if (phone.getIndPhonePref() == '1') {
          selectedPhone = phone;
        }
      });
    }

    if (!selectedPhone) {
      selectedPhone = this.getHomePhone();
    }

    if (!selectedPhone && this.spouse) {
      selectedPhone = this.spouse.getHomePhone();
    }

    return selectedPhone;
  }

  public getCellPhone() {
    let selectedPhone = null;

    if (this.phones) {
      this.phones.getItems().forEach((phone) => {
        /*if (phone.getIndPhonePref() && !phone.getIndPhoneDnc()) {
                    selectedPhone = phone;
                }*/
        if (phone.getIndPhoneType() == 'Cell') {
          selectedPhone = phone;
        }
      });
    }
    return selectedPhone;
  }

  public getMostRecentPolicyByType(type) {
    const policies = this.policies.getItems();

    if (policies.length === 0) {
      return null;
    }

    const mathchingPolicies = policies.filter((item) => {
      return item.getPolicyType() == type;
    });

    if (mathchingPolicies.length === 0) {
      return null;
    }

    let highestIdObject = mathchingPolicies[0];
    for (let i = 1; i < mathchingPolicies.length; i++) {
      if (mathchingPolicies[i].getPolicyId() > highestIdObject.getPolicyId()) {
        highestIdObject = mathchingPolicies[i];
      }
    }
    return highestIdObject;
  }

  public getHomePhone() {
    let selectedPhone = null;

    if (this.phones) {
      this.phones.getItems().forEach((phone) => {
        if (phone.getIndPhoneType() === 'Home') {
          selectedPhone = phone;
        }
      });
    }

    return selectedPhone;
  }

  public getAnyPhone() {
    let selectedPhone = null;

    selectedPhone = this.getDefaultPhone();

    if (!selectedPhone) {
      if (this.phones) {
        const anyPhone = this.phones.getItems()[0];

        if (anyPhone && !anyPhone.getIndPhoneDnc()) {
          selectedPhone = anyPhone;
        }
      }

      if (!selectedPhone && this.spouse) {
        if (this.spouse.getPhones()) {
          const spousePhones = this.spouse.getPhones().getItems();

          if (spousePhones.length > 0) {
            spousePhones.forEach((phone) => {
              if (phone.getIndPhonePref() == 1) {
                selectedPhone = phone;
              } else {
                selectedPhone = phone;
              }
            });
          }
        }
      }
    }

    return selectedPhone;
  }

  public getRecentPortalLog() {
    let recentPortalLog = null;

    if (this.portalLogs.getItems().length > 0) {
      recentPortalLog = this.portalLogs.getItems()[0].getLoginDateTime();
    }
    return recentPortalLog;
  }

  public getIndId() {
    return this.indId;
  }

  public getOwnerId() {
    return this.ownerId;
  }

  public setOwnerId(ownerId) {
    this.ownerId = ownerId;

    return this;
  }

  public getIndGender() {
    return this.indGender;
  }

  public setIndGender(indGender) {
    this.indGender = indGender;

    return this;
  }

  public getIndStatus() {
    return this.indStatus;
  }

  public setIndStatus(indStatus) {
    this.indStatus = indStatus;

    return this;
  }

  public getIndPrefix(): IndPrefix {
    return this.indPrefix;
  }

  public renderIndPrefix() {
    return this.indPrefix != null ? this.indPrefix + '.' : this.indPrefix;
  }

  public setIndPrefix(indPrefix) {
    this.indPrefix = indPrefix;

    return this;
  }

  public getIndName(nickname: number = 0) {
    return (
      (nickname && this.getIndNickname() !== '' && this.getIndNickname() != null
        ? this.getIndNickname()
        : this.getIndFirstname() || '') +
      ' ' +
      (this.getIndLastname() || '') +
      ' ' +
      (this.getIndSuffix() !== null ? this.getIndSuffix() : '')
    ).trim();
  }

  public getIndNameProper(nickname: number = 0) {
    return (
      (this.renderIndPrefix() || '') +
      ' ' +
      (nickname && this.getIndNickname() !== '' && this.getIndNickname() != null
        ? this.getIndNickname()
        : this.getIndFirstname() || '') +
      ' ' +
      (this.getIndLastname() || '') +
      ' ' +
      (this.getIndSuffix() !== null ? this.getIndSuffix() : '')
    ).trim();
  }

  public getIndJointSpouseName(nickname: number = 0) {
    const spouse = this.getSpouse();

    if (spouse && spouse.getIndStatus() !== 'Deceased' && spouse.getIndId() !== this.getIndId()) {
      if (spouse.getIndLastname() === this.getIndLastname()) {
        let suffix = '';
        const indNameString =
          nickname && this.getIndNickname() !== '' && this.getIndNickname() != null
            ? this.getIndNickname()
            : this.getIndFirstname() || '';

        const spouseNameString =
          nickname && spouse.getIndNickname() !== '' && spouse.getIndNickname() != null
            ? spouse.getIndNickname()
            : spouse.getIndFirstname() || '';

        let assembledNameString = indNameString + ' & ' + spouseNameString;

        this.getIndGender() != 'M'
          ? spouse.getIndGender() == 'M'
            ? (assembledNameString = spouseNameString + ' & ' + indNameString)
            : ''
          : '';

        this.getIndSuffix() !== null
          ? ((assembledNameString = spouseNameString + ' & ' + indNameString),
            (suffix = this.getIndSuffix()))
          : spouse.getIndSuffix() !== null
            ? ((assembledNameString = indNameString + ' & ' + spouseNameString),
              (suffix = spouse.getIndSuffix()))
            : '';

        assembledNameString += ' ' + this.getIndLastname() + ' ' + suffix;

        return assembledNameString.trim();
      } else {
        return this.getIndName(nickname) + '& ' + spouse.getIndName(nickname).trim();
      }
    }

    return this.getIndName(nickname);
  }

  public getIndJointSpouseNameProper(nickname: number = 0) {
    const spouse = this.getSpouse();

    if (spouse && spouse.getIndStatus() !== 'Deceased' && spouse.getIndId() !== this.getIndId()) {
      if (spouse.getIndLastname() === this.getIndLastname()) {
        let firstPrefix = this.renderIndPrefix();
        let secondPrefix = spouse.renderIndPrefix();
        let nameString =
          (nickname && this.getIndNickname() !== '' && this.getIndNickname() != null
            ? this.getIndNickname()
            : this.getIndFirstname() || '') +
          ' ' +
          this.getIndLastname();
        let nameAssembled = '';

        this.getIndGender() != 'M'
          ? spouse.getIndGender() == 'M'
            ? ((firstPrefix = spouse.renderIndPrefix() || ''),
              (secondPrefix = this.renderIndPrefix() || ''),
              (nameString =
                (nickname && spouse.getIndNickname() !== '' && spouse.getIndNickname() != null
                  ? spouse.getIndNickname()
                  : spouse.getIndFirstname() || '') +
                ' ' +
                spouse.getIndLastname()))
            : ''
          : '';

        nameAssembled = firstPrefix + ' & ' + secondPrefix + ' ' + nameString;
        return nameAssembled.trim();
      } else {
        return (
          (this.renderIndPrefix() || '') +
          ' ' +
          this.getIndName(nickname) +
          '&' +
          (spouse.renderIndPrefix() || '') +
          ' ' +
          spouse.getIndName(nickname).trim()
        );
      }
    }

    return this.getIndName(nickname);
  }

  public getIndJointSpouseFirstName(nickname: number = 0) {
    const spouse = this.getSpouse();

    if (spouse && spouse.getIndStatus() !== 'Deceased' && spouse.getIndId() !== this.getIndId()) {
      const suffix = '';
      const indNameString =
        nickname && this.getIndNickname() !== '' && this.getIndNickname() != null
          ? this.getIndNickname()
          : this.getIndFirstname() || '';

      const spouseNameString =
        nickname && spouse.getIndNickname() !== '' && spouse.getIndNickname() != null
          ? spouse.getIndNickname()
          : spouse.getIndFirstname() || '';

      const assembledNameString = indNameString + ' & ' + spouseNameString;

      return assembledNameString.trim();
    }

    return this.getIndFirstname();
  }

  public getIndFirstname() {
    return this.indFirstname;
  }

  public setIndFirstname(indFirstname) {
    this.indFirstname = indFirstname;

    return this;
  }

  public getIndMiddlename() {
    return this.indMiddlename;
  }

  public setIndMiddlename(indMiddlename) {
    this.indMiddlename = indMiddlename;

    return this;
  }

  public getIndLastname() {
    return this.indLastname;
  }

  public setIndLastname(indLastname) {
    this.indLastname = indLastname;

    return this;
  }

  public getIndSuffix() {
    return this.indSuffix;
  }

  public setIndSuffix(indSuffix) {
    this.indSuffix = indSuffix;

    return this;
  }

  public getIndNickname() {
    return this.indNickname;
  }

  public setIndNickname(indNickname) {
    this.indNickname = indNickname;

    return this;
  }

  public getIndDob() {
    return this.indDob;
  }

  public getIndDobFormatted() {
    let dob = '';
    if (this.indDob != '' && this.indDob) {
      dob = this.indDob.split('-');
      dob = dob[1] + '/' + dob[2] + '/' + dob[0];

      this.indDobFormatted = dob;
    }

    return this.indDobFormatted;
  }

  public setIndDobFormatted(indDobFormatted) {
    this.indDobFormatted = indDobFormatted;

    return this;
  }

  public setIndDob(indDob) {
    this.indDob = indDob;

    return this;
  }

  public getIndSource(): number {
    return this.indSource;
  }

  public setIndSource(indSource) {
    this.indSource = indSource;

    return this;
  }

  public getIndSpouse() {
    return this.indSpouse;
  }

  public setIndSpouse(indSpouse) {
    this.indSpouse = indSpouse;

    return this;
  }

  public getIndDesignation() {
    return this.indDesignation;
  }

  public setIndDesignation(indDesignation) {
    this.indDesignation = indDesignation;

    return this;
  }

  public getIndResident() {
    return this.indResident;
  }

  public setIndResident(indResident) {
    this.indResident = indResident;

    return this;
  }

  public getIndOccupation() {
    return this.indOccupation;
  }

  public setIndOccupation(indOccupation) {
    this.indOccupation = indOccupation;

    return this;
  }

  public getIndFlagged() {
    return this.indFlagged;
  }

  public setIndFlagged(indFlagged) {
    this.indFlagged = indFlagged;

    return this;
  }

  public getIndFacts() {
    return this.indFacts;
  }

  public setIndFacts(indFacts) {
    this.indFacts = indFacts;

    return this;
  }

  public getIndMaritalStatus() {
    return this.indMaritalStatus;
  }

  public setIndMaritalStatus(indMaritalStatus) {
    this.indMaritalStatus = indMaritalStatus;

    return this;
  }

  public getPartyId() {
    return this.partyId;
  }

  public setPartyId(partyId) {
    this.partyId = partyId;

    return this;
  }

  public getExternalId() {
    return this.externalId;
  }

  public getLoa() {
    return this.loa;
  }

  public setLoa(loa) {
    this.loa = loa;

    return this;
  }

  public getIndReferral() {
    return this.indReferral;
  }

  public setIndReferral(indReferral) {
    this.indReferral = indReferral;

    return this;
  }

  public getIndReferralTo() {
    return this.indReferralTo;
  }

  public setIndReferralTo(indReferralTo) {
    this.indReferralTo = indReferralTo;

    return this;
  }

  public getIndPreferredContactMethod() {
    return this.indPreferredContactMethod;
  }

  public setIndPreferredContactMethod(indPreferredContactMethod) {
    this.indPreferredContactMethod = indPreferredContactMethod;

    return this;
  }

  public getIndProfilePic() {
    return this.indProfilePic;
  }

  public setIndProfilePic(indProfilePic) {
    this.indProfilePic = indProfilePic;

    return this;
  }

  public getIndAge(indDob = '') {
    if (indDob == '' || indDob == null) {
      return '';
    } else {
      const newIndDob = new Date(indDob);
      const month_diff = Date.now() - newIndDob.getTime();
      const age_dt = new Date(month_diff);
      const year = age_dt.getUTCFullYear();
      const age = Math.abs(year - 1970);
      this.indAge = age;
      return this.indAge;
    }
  }

  public getIndIdCrypto() {
    return this.indIdCrypto;
  }

  public getDateTimeCreated() {
    return this.dateTimeCreated;
  }

  public getLastUpdateTime() {
    return this.lastUpdateTime;
  }

  public getIndConnections() {
    return this.connections;
  }

  public getDependents() {
    return this.dependents;
  }

  public getFlags() {
    return this.flags;
  }

  public getUserSoa() {
    return this.soas;
  }
}
